import React, { useState, useEffect } from 'react'
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/apiFetch'
import apiPath from "../../utils/pathObj";
import OButton from 'components/reusable/OButton'
import { useTranslation } from 'react-i18next'
import { AiFillEdit, AiFillDelete } from 'react-icons/ai'
import useToastContext from 'hooks/useToastContext'
import helpers from 'utils/helpers'
import { isEmpty } from 'lodash'

const LeavePolicyManager = () => {
  const { t } = useTranslation()
  const notification = useToastContext()
  const [policies, setPolicies] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    leaveType: 'sick',
    allotmentPerMonth: 1.5,
    maxCarryForward: 10,
    maxDaysPerYear: 18,
    isPaid: true,
    requiresApproval: true,
    minDaysBeforeApply: 0
  })

  const getPolicies = async () => {
    try {
      const res = await apiGet(apiPath.leavePolicyList, {})
      setPolicies(res?.data?.results || [])
    } catch (error) {
      console.log('Error fetching policies:', error)
    }
  }

  useEffect(() => {
    getPolicies()
  }, [])

  const handleAddNew = () => {
    setEditingPolicy(null)
    setFormData({
      name: '',
      leaveType: 'sick',
      allotmentPerMonth: 1.5,
      maxCarryForward: 10,
      maxDaysPerYear: 18,
      isPaid: true,
      requiresApproval: true,
      minDaysBeforeApply: 0
    })
    setShowForm(true)
  }

  const handleEdit = (policy) => {
    setEditingPolicy(policy)
    setFormData({
      name: policy.name,
      leaveType: policy.leaveType,
      allotmentPerMonth: policy.allotmentPerMonth,
      maxCarryForward: policy.maxCarryForward,
      maxDaysPerYear: policy.maxDaysPerYear,
      isPaid: policy.isPaid,
      requiresApproval: policy.requiresApproval,
      minDaysBeforeApply: policy.minDaysBeforeApply || 0
    })
    setShowForm(true)
  }

  const handleDelete = async (policyId) => {
    helpers.alertFunction(
      t('CONFIRM_DELETE_POLICY'),
      policyId,
      async (id) => {
        try {
          const res = await apiDelete(`${apiPath.leavePolicyDelete}/${id}`)
          if (res?.data?.success) {
            notification.success(res?.data?.message)
            getPolicies()
          } else {
            notification.error(res?.data?.message)
          }
        } catch (error) {
          notification.error(t('SOMETHING_WENT_WRONG'))
        }
      },
      true
    )
  }

  const handleSubmit = async () => {
    if (!formData.name) {
      notification.error(t('PLEASE_ENTER_POLICY_NAME'))
      return
    }

    try {
      setIsLoading(true)
      const payload = {
        ...formData,
        allotmentPerMonth: Number(formData.allotmentPerMonth),
        maxCarryForward: Number(formData.maxCarryForward),
        maxDaysPerYear: Number(formData.maxDaysPerYear),
        minDaysBeforeApply: Number(formData.minDaysBeforeApply)
      }

      let res
      if (editingPolicy) {
        res = await apiPut(`${apiPath.leavePolicyUpdate}/${editingPolicy._id}`, payload)
      } else {
        res = await apiPost(apiPath.leavePolicyCreate, payload)
      }

      if (res?.data?.success) {
        notification.success(res?.data?.message)
        getPolicies()
        setShowForm(false)
        setEditingPolicy(null)
      } else {
        notification.error(res?.data?.message)
      }
    } catch (error) {
      notification.error(t('SOMETHING_WENT_WRONG'))
    } finally {
      setIsLoading(false)
    }
  }

  const leaveTypes = [
    { value: 'sick', label: t('SICK_LEAVE') },
    { value: 'casual', label: t('CASUAL_LEAVE') },
    { value: 'earned', label: t('EARNED_LEAVE') },
    { value: 'unpaid', label: t('UNPAID_LEAVE') },
    { value: 'emergency', label: t('EMERGENCY_LEAVE') }
  ]

  return (
    <div className='bg-[#F9F9F9]'>
      <div className='bg-white border border-[#E9EDF9] rounded-lg'>
        {/* Header */}
        <div className='p-4 border-b flex items-center justify-between'>
          <h3 className='font-bold text-lg text-gray-800'>{t('LEAVE_POLICIES')}</h3>
          <OButton
            label={`+ ${t('ADD_POLICY')}`}
            onClick={handleAddNew}
            style={{ borderRadius: '0.5rem' }}
          />
        </div>

        {/* Policy List */}
        <div className='p-3'>
          <div className='border rounded-lg overflow-x-auto'>
            <table className='w-full text-xs text-left text-[#A5A5A5]'>
              <thead className='text-gray-900 bg-[#E1E6EE]'>
                <tr>
                  <th className='px-4 py-3'>{t('POLICY_NAME')}</th>
                  <th className='px-4 py-3'>{t('LEAVE_TYPE')}</th>
                  <th className='px-4 py-3'>{t('PER_MONTH')}</th>
                  <th className='px-4 py-3'>{t('MAX_YEAR')}</th>
                  <th className='px-4 py-3'>{t('CARRY_FORWARD')}</th>
                  <th className='px-4 py-3'>{t('PAID')}</th>
                  <th className='px-4 py-3 text-center'>{t('O_ACTION')}</th>
                </tr>
              </thead>
              <tbody>
                {policies.length === 0 ? (
                  <tr>
                    <td colSpan={7} className='text-center py-8 text-gray-400'>
                      {t('NO_POLICIES_FOUND')}
                    </td>
                  </tr>
                ) : (
                  policies.map((policy) => (
                    <tr key={policy._id} className='bg-white border-b hover:bg-gray-50'>
                      <td className='px-4 py-3 text-gray-900 font-medium'>{policy.name}</td>
                      <td className='px-4 py-3'>
                        <span className='px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700'>
                          {policy.leaveType}
                        </span>
                      </td>
                      <td className='px-4 py-3'>{policy.allotmentPerMonth} days</td>
                      <td className='px-4 py-3'>{policy.maxDaysPerYear} days</td>
                      <td className='px-4 py-3'>{policy.maxCarryForward} days</td>
                      <td className='px-4 py-3'>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          policy.isPaid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {policy.isPaid ? t('YES') : t('NO')}
                        </span>
                      </td>
                      <td className='px-4 py-3 text-center'>
                        <div className='flex justify-center gap-2'>
                          <button
                            onClick={() => handleEdit(policy)}
                            className='p-1.5 hover:bg-blue-50 rounded text-blue-600'
                          >
                            <AiFillEdit className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => handleDelete(policy._id)}
                            className='p-1.5 hover:bg-red-50 rounded text-red-600'
                          >
                            <AiFillDelete className='w-4 h-4' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add/Edit Form Modal */}
        {showForm && (
          <>
            <div className='fixed inset-0 z-[60] bg-black opacity-40' onClick={() => setShowForm(false)} />
            <div className='fixed inset-0 z-[70] flex items-center justify-center'>
              <div className='bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto'>
                <div className='p-4 border-b flex items-center justify-between'>
                  <h3 className='font-bold text-lg'>
                    {editingPolicy ? t('EDIT_POLICY') : t('ADD_NEW_POLICY')}
                  </h3>
                  <button onClick={() => setShowForm(false)} className='text-2xl text-gray-400 hover:text-gray-600'>
                    ×
                  </button>
                </div>

                <div className='p-5'>
                  <div className='grid grid-cols-2 gap-4'>
                    {/* Policy Name */}
                    <div className='col-span-2'>
                      <label className='block text-xs text-gray-500 mb-1'>{t('POLICY_NAME')} *</label>
                      <input
                        type='text'
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className='w-full p-3 text-sm border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0'
                        placeholder={t('ENTER_POLICY_NAME')}
                      />
                    </div>

                    {/* Leave Type */}
                    <div>
                      <label className='block text-xs text-gray-500 mb-1'>{t('LEAVE_TYPE')}</label>
                      <select
                        value={formData.leaveType}
                        onChange={(e) => setFormData({ ...formData, leaveType: e.target.value })}
                        className='w-full p-3 text-sm border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0'
                      >
                        {leaveTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Paid/Unpaid */}
                    <div>
                      <label className='block text-xs text-gray-500 mb-1'>{t('PAID_UNPAID')}</label>
                      <select
                        value={formData.isPaid}
                        onChange={(e) => setFormData({ ...formData, isPaid: e.target.value === 'true' })}
                        className='w-full p-3 text-sm border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0'
                      >
                        <option key='true' value='true'>{t('PAID')}</option>
                        <option key='false'  value='false'>{t('UNPAID')}</option>
                      </select>
                    </div>

                    {/* Days Per Month */}
                    <div>
                      <label className='block text-xs text-gray-500 mb-1'>{t('DAYS_PER_MONTH')}</label>
                      <input
                        type='number'
                        step='0.5'
                        value={formData.allotmentPerMonth}
                        onChange={(e) => setFormData({ ...formData, allotmentPerMonth: e.target.value })}
                        className='w-full p-3 text-sm border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0'
                      />
                    </div>

                    {/* Max Per Year */}
                    <div>
                      <label className='block text-xs text-gray-500 mb-1'>{t('MAX_DAYS_PER_YEAR')}</label>
                      <input
                        type='number'
                        value={formData.maxDaysPerYear}
                        onChange={(e) => setFormData({ ...formData, maxDaysPerYear: e.target.value })}
                        className='w-full p-3 text-sm border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0'
                      />
                    </div>

                    {/* Max Carry Forward */}
                    <div>
                      <label className='block text-xs text-gray-500 mb-1'>{t('MAX_CARRY_FORWARD')}</label>
                      <input
                        type='number'
                        value={formData.maxCarryForward}
                        onChange={(e) => setFormData({ ...formData, maxCarryForward: e.target.value })}
                        className='w-full p-3 text-sm border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0'
                      />
                    </div>

                    {/* Min Days Before Apply */}
                    <div>
                      <label className='block text-xs text-gray-500 mb-1'>{t('MIN_DAYS_BEFORE_APPLY')}</label>
                      <input
                        type='number'
                        value={formData.minDaysBeforeApply}
                        onChange={(e) => setFormData({ ...formData, minDaysBeforeApply: e.target.value })}
                        className='w-full p-3 text-sm border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0'
                      />
                    </div>

                    {/* Requires Approval */}
                    <div className='col-span-2'>
                      <label className='flex items-center gap-2 cursor-pointer'>
                        <input
                          type='checkbox'
                          checked={formData.requiresApproval}
                          onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
                          className='w-4 h-4'
                        />
                        <span className='text-sm text-gray-700'>{t('REQUIRES_APPROVAL')}</span>
                      </label>
                    </div>
                  </div>

                  <div className='flex justify-end gap-3 mt-6'>
                    <button
                      onClick={() => setShowForm(false)}
                      className='px-6 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300'
                    >
                      {t('O_CANCEL')}
                    </button>
                    {isLoading ? (
                      <div className='px-6 py-2 bg-[#4F46E5] rounded-md'>
                        <div className='loading-spinner h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin'></div>
                      </div>
                    ) : (
                      <button
                        onClick={handleSubmit}
                        className='px-6 py-2 text-sm bg-[#4F46E5] text-white rounded-md hover:bg-[#4338CA]'
                      >
                        {editingPolicy ? t('O_UPDATE') : t('O_ADD')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default LeavePolicyManager